import { useEffect, useRef } from "react";
import type { Session, User } from "@supabase/supabase-js";
import {
  COOKIE_CONSENT_EVENT,
  type ConsentState,
  ensurePostHogInitialized,
  hasAnalyticsConsent,
  resetPostHog,
} from "@/lib/posthog";

function getIdentifyProperties(user: User): Record<string, unknown> | undefined {
  const properties: Record<string, unknown> = {};
  if (typeof user.email === "string" && user.email) {
    properties.email = user.email;
  }
  const fullName = typeof user.user_metadata?.full_name === "string"
    ? user.user_metadata.full_name.trim()
    : "";
  if (fullName) {
    properties.full_name = fullName;
  }
  return Object.keys(properties).length > 0 ? properties : undefined;
}

async function getSupabaseClient() {
  const { supabase } = await import("@/lib/supabaseClient");
  return supabase;
}

async function initializePostHogForSession(session: Session | null) {
  if (!hasAnalyticsConsent()) return false;

  const posthog = await ensurePostHogInitialized();
  if (!posthog) return false;
  posthog.opt_in_capturing();

  const user = session?.user ?? null;
  if (!user?.id) return false;

  posthog.identify(user.id, getIdentifyProperties(user));
  return true;
}

export default function PostHogLifecycle() {
  const lastIdentifiedUserIdRef = useRef<string | null>(null);
  const isLifecycleActiveRef = useRef(false);
  const authSubscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);

  useEffect(() => {
    let disposed = false;

    const syncCurrentSession = async () => {
      if (!hasAnalyticsConsent()) return;

      const posthog = await ensurePostHogInitialized();
      if (!posthog || disposed) return;
      posthog.opt_in_capturing();

      const supabase = await getSupabaseClient();
      if (disposed) return;

      const { data, error } = await supabase.auth.getSession();
      if (error || disposed) return;

      const user = data.session?.user ?? null;
      if (!user?.id) return;
      if (lastIdentifiedUserIdRef.current === user.id) return;

      posthog.identify(user.id, getIdentifyProperties(user));
      lastIdentifiedUserIdRef.current = user.id;
    };

    const activateLifecycle = async () => {
      if (disposed || isLifecycleActiveRef.current || !hasAnalyticsConsent()) return;

      isLifecycleActiveRef.current = true;
      const supabase = await getSupabaseClient();
      if (disposed) return;

      await syncCurrentSession();

      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, nextSession) => {
        if (nextSession?.user?.id) {
          if (lastIdentifiedUserIdRef.current === nextSession.user.id) return;
          void initializePostHogForSession(nextSession).then((didIdentify) => {
            if (didIdentify) {
              lastIdentifiedUserIdRef.current = nextSession.user.id;
            }
          });
          return;
        }

        lastIdentifiedUserIdRef.current = null;
        void resetPostHog();
      });

      authSubscriptionRef.current = subscription;
    };

    const handleConsentChanged = (event: Event) => {
      const detail = (event as CustomEvent<ConsentState>).detail;
      if (detail?.analytics) {
        void activateLifecycle();
      }
    };

    if (hasAnalyticsConsent()) {
      void activateLifecycle();
    }

    window.addEventListener(COOKIE_CONSENT_EVENT, handleConsentChanged);

    return () => {
      disposed = true;
      authSubscriptionRef.current?.unsubscribe();
      window.removeEventListener(COOKIE_CONSENT_EVENT, handleConsentChanged);
    };
  }, []);

  return null;
}
